import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, day } from "../../api.js";
import { useResource } from "../useResource.js";
import { SubmittedDocs } from "../RegdocView.jsx";
import { shortName } from "../common.jsx";
import { ROW_TITLES } from "../../../../lib/vat/rows.js";
import { vatDeclarationHtml, vatD1Html, vatD2Html } from "../../../../lib/vat/form.js";
import formCssUrl from "../../../../lib/vat/form.css?url";
import { ScaledForm } from "../pn/PnForm.jsx";
import VatSign from "./VatSign.jsx";
import "./vat.css";

// «Декларація з ПДВ»: ① місяць → ② перевірте операції (рішення щодо кожної зупиненої ПН, кредит, імпорт) →
// ③ результат → ④ перегляд рядків і Д1, XML. Чернетка зберігається лише в eCabinet; у ДПС нічого не надсилається.
const STEPS = ["Місяць", "Операції", "Результат", "Перегляд"];
const uah = (v) => `${Math.round(Number(v) || 0).toLocaleString("uk-UA")} грн`;
const uah2 = (v) => `${(Number(v) || 0).toLocaleString("uk-UA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;
// Велика сума підсумку: той самий текст, що й uah(v), одиниця — окремим елементом (у вигляді minimal — дрібніша)
const UahBig = ({ v }) => <b className="vat-sum">{uah(v).slice(0, -4)}<span className="vat-unit"> грн</span></b>;
const mm = (ym) => (ym ? `${ym.slice(5, 7)}.${ym.slice(0, 4)}` : "");
const MONTHS = ["січень", "лютий", "березень", "квітень", "травень", "червень", "липень", "серпень", "вересень", "жовтень", "листопад", "грудень"];
const monthName = (ym) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
const sumLines = (s) => s.lines.reduce((a, l) => ({ base: a.base + l.base, vat: a.vat + l.base * l.rate / 100 }), { base: 0, vat: 0 });

export default function VatSection({ companyId, company, draftId, version }) {
  if (draftId) return <VatWizard companyId={companyId} company={company} draftId={draftId} reportsVersion={version} />;
  return <VatPeriods companyId={companyId} company={company} />;
}

// --- Крок 1: місяць ---
function VatPeriods({ companyId, company }) {
  const r = useResource(`/api/companies/${companyId}/vat`);
  const [busy, setBusy] = useState("");
  const canEdit = ["preparer", "submitter", "owner"].includes(company.role);
  const open = async (period) => {
    setBusy(period);
    try { const x = await api(`/api/companies/${companyId}/vat/drafts`, { method: "POST", body: { period } }); location.hash = `#/c/${companyId}/reports/vat/${x.draft.id}`; }
    catch (e) { window.alert(e.message); setBusy(""); }
  };
  if (r.error) return <p className="error">{r.error.message}</p>;
  if (!r.data) return <p className="muted">Завантажуємо…</p>;
  const { periods, suggested, today, declsKnown, vatFrom } = r.data;
  const sug = periods.find((p) => p.period === suggested);
  return (
    <>
    <section className="card vat">
      <h3>За який місяць?</h3>
      {!declsKnown && <p className="notice small">Поданих декларацій ще не завантажено — оновіть розділ «СМКОР» (він читає «Перегляд звітності»), інакше залишок з попереднього місяця невідомий.</p>}
      {vatFrom && <p className="muted small">Платник ПДВ з {day(vatFrom)} — раніших місяців не показуємо.</p>}
      {!sug && <p className="pn-ok">✓ За останні місяці все подано або надіслано — нового складати не треба.</p>}
      {sug && (
        <div className="vat-suggest">
          <div><b>{monthName(sug.period)}</b> — подати до <b>{day(sug.deadline)}</b>{sug.deadline < today ? " (строк минув)" : ""}
            {sug.draft && <span className="muted small"> · є чернетка</span>}</div>
          <button type="button" className="btn" disabled={!canEdit || busy} onClick={() => open(sug.period)}>{sug.draft ? "Продовжити" : "Скласти декларацію"} →</button>
        </div>
      )}
      <details className="vat-periods small"><summary>Інші місяці</summary>
        <ul>{periods.map((p) => (
          <li key={p.period}>
            <span className="vat-p">{mm(p.period)}</span>
            {p.submitted ? <span>подано {day(p.submitted.dget)} · {p.submitted.payable ? `до сплати ${uah(p.submitted.payable)}` : p.submitted.carry ? `перенос ${uah(p.submitted.carry)}` : "0"}</span>
              : p.sent ? <span>{p.sent.state === "accepted" ? "✓ прийнято" : p.sent.state === "rejected" ? "не прийнято ДПС" : "надіслано"}{p.sent.at ? ` ${day(p.sent.at.slice(0, 10))}` : ""}{p.sent.state === "sent" ? " · чекаємо квитанцію №2" : ""}</span>
              : <span className="muted">не подано · строк {day(p.deadline)}</span>}
            {p.draft ? <a href="#" onClick={(e) => { e.preventDefault(); open(p.period); }}>чернетка →</a>
              : !p.submitted && !p.sent && canEdit && <button type="button" className="linkish" onClick={() => open(p.period)}>скласти</button>}
          </li>
        ))}</ul>
      </details>
    </section>
    <SubmittedDocs companyId={companyId} company={company} title="Подані декларації з ПДВ" items={periods.filter((p) => p.submitted).map((p) => ({ key: p.period, period: monthName(p.period),
      dget: day(p.submitted.dget), regdoc: p.submitted.regdoc,
      summary: p.submitted.payable ? `до сплати ${uah(p.submitted.payable)}` : p.submitted.carry ? `перенос ${uah(p.submitted.carry)}` : "0" }))} />
    </>
  );
}

// --- Майстер ---
function VatWizard({ companyId, company, draftId, reportsVersion }) {
  const canEdit = ["preparer", "submitter", "owner"].includes(company.role);
  const [key, setKey] = useState(0);
  const r = useResource(`/api/companies/${companyId}/vat/drafts/${draftId}`, key);
  const [doc, setDoc] = useState(null);
  const [step, setStep] = useState(2);
  const [save, setSave] = useState("saved");
  const version = useRef(0), pending = useRef(null), saving = useRef(false), timer = useRef(null);
  useEffect(() => { if (r.data && doc == null) { setDoc(r.data.draft.doc || {}); version.current = r.data.draft.version; } }, [r.data]);
  const flush = useCallback(async () => {
    if (saving.current || !pending.current) return;
    saving.current = true; const d = pending.current; pending.current = null; setSave("saving");
    try {
      const x = await api(`/api/companies/${companyId}/vat/drafts/${draftId}`, { method: "PUT", body: { doc: d, version: version.current } });
      version.current = x.draft.version; setSave("saved");
      if (!pending.current) setKey((k) => k + 1);
    } catch (e) { setSave(e instanceof ApiError && e.status === 409 ? "conflict" : "error"); }
    finally { saving.current = false; if (pending.current) flush(); }
  }, [companyId, draftId]);
  const update = (fn) => setDoc((d) => { const n = fn(d); pending.current = n; clearTimeout(timer.current); timer.current = setTimeout(flush, 500); return n; });
  if (r.error && !r.data) return <p className="error">{r.error.message}</p>;
  if (!r.data || !doc) return <p className="muted">Збираємо дані за місяць…</p>;
  const { context: ctx, result: res } = r.data;
  const go = (n) => setStep(n);
  return (
    <section className="vat">
      <div className="vat-head">
        <h3>Декларація з ПДВ за {monthName(ctx.period)}</h3>
        <span className="muted small">подати до {day(ctx.deadline)} · {save === "saving" ? "зберігаємо…" : save === "saved" ? "чернетка збережена" : save === "conflict" ? "змінено в іншій вкладці — оновіть сторінку" : "не вдалося зберегти"}{r.updating ? " · перераховуємо…" : ""}</span>
        <a className="small" href={`#/c/${companyId}/reports/vat`}>Усі місяці</a>
      </div>
      <ol className="pn-steps vat-steps">{STEPS.map((s, i) => (
        <li key={s} className={step === i + 1 ? "on" : step > i + 1 ? "done" : ""}>
          <button type="button" onClick={() => (i === 0 ? (location.hash = `#/c/${companyId}/reports/vat`) : go(i + 1))} aria-current={step === i + 1 ? "step" : undefined}>
            <span className="pn-step-n">{step > i + 1 ? "✓" : i + 1}</span>{s}</button></li>
      ))}</ol>
      <Totals res={res} />
      <fieldset disabled={!canEdit} className="vat-main">
        {step === 2 && <StepOps ctx={ctx} res={res} doc={doc} update={update} advice={r.data.advice} />}
        {step === 3 && <StepResult ctx={ctx} res={res} go={go} />}
        {step === 4 && <StepReview companyId={companyId} draftId={draftId} data={r.data} version={version.current} onReviewed={() => setKey((k) => k + 1)} />}
        {step === 4 && r.data.draft.reviewed?.sha256 && r.data.draft.reviewed.sha256 === r.data.sha256 && <VatSign companyId={companyId} company={company} draftId={draftId} period={ctx.period} version={reportsVersion} />}
      </fieldset>
      <div className="actions pn-nav">
        {step > 2 && <button type="button" className="btn btn-secondary" onClick={() => go(step - 1)}>← Назад</button>}
        {step < 4 && <button type="button" className="btn" onClick={() => go(step + 1)}>{step === 2 ? "Далі: результат →" : "Далі: перегляд декларації →"}</button>}
      </div>
    </section>
  );
}

// Три суми на кожному кроці: ПДВ з продажів, кредит, до сплати або перенос
function Totals({ res }) {
  const t = res.totals;
  return (
    <div className="vat-totals" aria-live="polite">
      <div><span className="muted small">ПДВ з продажів (р.9)</span><UahBig v={t.obligations} /></div>
      <div><span className="muted small">Кредит (р.17)</span><UahBig v={t.credit} /></div>
      <div className={t.payable ? "pay" : "carry"}><span className="muted small">{t.payable ? "До сплати (р.18)" : "Перенос (р.21)"}</span><UahBig v={t.payable || t.carry} /></div>
    </div>
  );
}

// --- Крок 2: операції ---
function StepOps({ ctx, res, doc, update, advice }) {
  const reg = ctx.sales.filter((s) => !s.stopped), stopped = ctx.sales.filter((s) => s.stopped);
  const regSum = reg.reduce((a, s) => { const v = sumLines(s); return { base: a.base + v.base, vat: a.vat + v.vat }; }, { base: 0, vat: 0 });
  const dec = doc.stoppedDecisions || {};
  const setDec = (code, v) => update((d) => ({ ...d, stoppedDecisions: { ...(d.stoppedDecisions || {}), [code]: v } }));
  const sel = doc.credit || {}, rksel = doc.rk || {};
  const usable = ctx.credit.filter((g) => !g.expired), expired = ctx.credit.filter((g) => g.expired);
  const toggle = (field, g, on) => update((d) => { const m = { ...(d[field] || {}) }; if (on) m[g.key] = g.available; else delete m[g.key]; return { ...d, [field]: m }; });
  const all = (on) => update((d) => ({ ...d, credit: on ? Object.fromEntries(usable.map((g) => [g.key, g.available])) : {} }));
  const chosen = usable.filter((g) => sel[g.key] != null);
  const answers = doc.answers || {};
  return (
    <>
      <section className="card">
        <h4>Продажі за {monthName(ctx.period)}</h4>
        <p>Зареєстровані ПН: <b>{reg.length}</b> на {uah2(regSum.base)} без ПДВ, ПДВ <b>{uah2(regSum.vat)}</b> — включаються автоматично.</p>
        {reg.length > 0 && <details className="small"><summary>Перелік</summary><ul className="vat-list">{reg.map((s) => { const v = sumLines(s); return <li key={s.code}>№{s.nmr} від {day(s.date)} — {shortName(s.buyer)}: {uah2(v.base)}, ПДВ {uah2(v.vat)}</li>; })}</ul></details>}
      </section>

      {stopped.length > 0 && (
        <section className="card" aria-labelledby="vat-stopped-h">
          <h4 id="vat-stopped-h">Зупинені ПН — рішення щодо кожної ({stopped.filter((s) => dec[s.code] == null).length} без рішення)</h4>
          <p className="small muted">Реєстрацію цих ПН зупинено. Включити їх у зобов'язання цього місяця чи ні — вирішуєте ви; без рішення щодо кожної декларація не сформується.
            Включені — збільшують ПДВ до сплати зараз і потрапляють у додаток 1 (табл. 1.1). Не включені — якщо комісія зареєструє ПН, їх доведеться задекларувати уточнюючим розрахунком. Це пояснення, а не порада.</p>
          <ul className="vat-stopped">{stopped.map((s) => {
            const v = sumLines(s), st = s.stop;
            return (
              <li key={s.code} className={dec[s.code] == null ? "pending" : ""}>
                <div><b>№{s.nmr}</b> від {day(s.date)} — {shortName(s.buyer)} · {uah2(v.base)}, ПДВ {uah2(v.vat)}
                  <div className="muted small">{!st ? "стан розгляду невідомий" : st.decision ? (st.refused ? `відмова комісії ${day(st.decision)}` : `рішення ${day(st.decision)}`) : st.explained ? `пояснення подано ${day(st.explained)}, чекає рішення` : "пояснень не подано"}</div></div>
                <div className="vat-choice" role="radiogroup" aria-label={`ПН №${s.nmr}`}>
                  <label><input type="radio" name={`st-${s.code}`} checked={dec[s.code] === true} onChange={() => setDec(s.code, true)} /> включити</label>
                  <label><input type="radio" name={`st-${s.code}`} checked={dec[s.code] === false} onChange={() => setDec(s.code, false)} /> не включати</label>
                </div>
              </li>);
          })}</ul>
        </section>
      )}

      <section className="card" aria-labelledby="vat-credit-h">
        <h4 id="vat-credit-h">Податковий кредит — що включаєте цього місяця</h4>
        <p className="small muted">Зареєстровані вхідні ПН, ще не використані в поданих деклараціях (за додатками 1). Вибрано {chosen.length} з {usable.length}, ПДВ {uah2(chosen.reduce((a, g) => a + g.availableVat, 0))}.</p>
        {advice && <CreditAdvice advice={advice} ctx={ctx} doc={doc} update={update} />}
        <div className="row"><button type="button" className="btn btn-secondary btn-sm" onClick={() => all(true)}>Включити все доступне</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => all(false)}>Зняти все</button></div>
        <CreditMonths usable={usable} sel={sel} advice={advice} toggle={(g, on) => toggle("credit", g, on)}
          setMany={(list, on) => update((d) => { const m = { ...(d.credit || {}) }; for (const g of list) { if (on) m[g.key] = g.available; else delete m[g.key]; } return { ...d, credit: m }; })} />
        {expired.length > 0 && <details className="small"><summary>Строк 365 днів минув: {expired.length} груп на {uah2(expired.reduce((a, g) => a + g.availableVat, 0))} ПДВ</summary>
          <p className="muted">Ці залишки не використані в поданих деклараціях, а строк минув. Якщо ви включали їх в інший спосіб (наприклад, уточнюючим розрахунком) або строк зупинявся — перевірте вручну.</p>
          <ul className="vat-list">{expired.map((g) => <li key={g.key}>ПН №{g.nmr} від {day(g.date)} · {shortName(g.supplierName) || g.supplier} · {uah2(g.available)} (до {mm(g.deadline)})</li>)}</ul></details>}
      </section>

      {ctx.rk.length > 0 && (
        <section className="card">
          <h4>Розрахунки коригування від постачальників (р.14)</h4>
          <p className="small muted">РК на зменшення: покупець зменшує кредит у періоді складання РК, навіть якщо РК ще не зареєстровано. Відмітьте, які відображаєте зараз.</p>
          <ul className="vat-list">{ctx.rk.map((g) => (
            <li key={g.key}><label><input type="checkbox" checked={rksel[g.key] != null} onChange={(e) => toggle("rk", g, e.target.checked)} /> РК №{g.nmr} від {day(g.date)} · {shortName(g.supplierName) || g.supplier} · {uah2(g.available)}, ПДВ {uah2(g.availableVat)}</label>
              {g.ym < ctx.period && <span className="small warn"> — складено раніше ({mm(g.ym)})</span>}</li>))}</ul>
        </section>
      )}

      <section className="card">
        <h4>Імпорт (р.11)</h4>
        {ctx.imports && ctx.imports.length ? <p>Митні декларації в СЕА за місяць: {ctx.imports.length}, ПДВ {uah2(ctx.imports.reduce((a, x) => a + x.vat, 0))}.</p> : (
          <>
            <p className="small">{ctx.customsKnown ? "За цей місяць митних декларацій у СЕА немає." : "Митні декларації компанії ще не завантажено."} «Не завантажено» — не те саме, що нуль.</p>
            <label className="check"><input type="checkbox" checked={Boolean(doc.importsNone)} onChange={(e) => update((d) => ({ ...d, importsNone: e.target.checked }))} /> Імпорту цього місяця не було</label>
            <p className="small"><a href={location.hash.replace(/reports\/vat.*/, "customs")}>Завантажити МД →</a></p>
          </>
        )}
      </section>

      <section className="card">
        <h4>Чи були цього місяця такі операції?</h4>
        <p className="small muted">Їх ще не підтримує майстер — якщо були, декларацію поки складіть іншим способом (ми скажемо, що саме не так).</p>
        <ul className="vat-list">{Object.entries({ compensating: "Компенсуючі зобов'язання: товари чи послуги, використані не в оподатковуваних операціях (п.198.5, п.199.1)", export: "Експорт або операції за нульовою ставкою",
          exempt: "Операції, звільнені від ПДВ", nonResident: "Послуги від нерезидентів", cashMethod: "Касовий метод обліку", refund: "Хочете бюджетне відшкодування" }).map(([k, l]) => (
          <li key={k}><label className="check"><input type="checkbox" checked={answers[k] === true} onChange={(e) => update((d) => ({ ...d, answers: { ...(d.answers || {}), [k]: e.target.checked } }))} /> {l}</label></li>
        ))}</ul>
      </section>

      {res.rows.R190GB > 0 && (
        <section className="card">
          <h4>Від'ємне значення — сума за п.200-1.3</h4>
          <p className="small">Р.17 більший за р.9 на {uah(res.rows.R190GB)}. Сума за п.200¹.3 ПКУ — це реєстраційна сума ΣНакл на момент подання; від неї залежить розподіл між р.19.1 і р.20 (на перенос у р.21 не впливає).
            {advice?.regSum.fromSea && <> Зараз підставлено ΣНакл із СЕА: <b>{uah(advice.regSum.value)}</b> на {day(advice.regSum.at)} — перед поданням оновіть розділ «СЕА».</>}</p>
          <label className="field field-inline">Сума за п.200¹.3 (ΣНакл), грн
            <input inputMode="numeric" placeholder={advice?.regSum.value != null ? String(Math.round(advice.regSum.value)) : ""} value={doc.regSum2001 ?? ""} onChange={(e) => update((d) => ({ ...d, regSum2001: e.target.value === "" ? null : Number(e.target.value.replace(/\s/g, "")) || 0 }))} /></label>
        </section>
      )}
    </>
  );
}

// Кредит по кожній ПН, згрупований за місяцем складання: галочка на місяць і на кожну ПН
function CreditMonths({ usable, sel, advice, toggle, setMany }) {
  const months = [...new Set(usable.map((g) => g.ym))].sort();
  return (
    <div className="vat-months">{months.map((ym) => {
      const list = usable.filter((g) => g.ym === ym), on = list.filter((g) => sel[g.key] != null);
      const all = on.length === list.length, some = on.length > 0 && !all;
      const must = list.some((g) => advice?.mustNow.includes(g.key)), soon = list.some((g) => advice?.soon.includes(g.key));
      return (
        // Галочка місяця — поруч із summary, а не в ньому (інтерактивний елемент у summary недоступний читачам екрана)
        <div key={ym} className="vat-month-wrap">
        <input type="checkbox" className="vat-month-all" aria-label={`Усі ПН за ${mm(ym)}`} checked={all} ref={(el) => { if (el) el.indeterminate = some; }} onClick={(e) => e.stopPropagation()} onChange={(e) => setMany(list, e.target.checked)} />
        <details className="vat-month" open={must}>
          <summary>
            <b>{mm(ym)}</b><span className="small">ПН: {list.length} · доступно ПДВ {uah2(list.reduce((a, g) => a + g.availableVat, 0))}</span>
            <span className="small muted">вибрано {on.length}, ПДВ {uah2(on.reduce((a, g) => a + g.availableVat, 0))}</span>
            {must && <span className="small error">строк спливає цього місяця</span>}{!must && soon && <span className="small warn">строк спливає до 3 міс.</span>}
          </summary>
          <div className="table-wrap"><table className="vat-credit">
            <thead><tr><th><span className="sr-only">Включити</span></th><th>Постачальник</th><th>ПН</th><th>Зареєстровано</th><th>Доступно, без ПДВ</th><th>ПДВ</th><th>Включити до</th></tr></thead>
            <tbody>{list.map((g) => (
              <tr key={g.key} className={`${sel[g.key] != null ? "on" : ""}${advice?.mustNow.includes(g.key) ? " must" : advice?.soon.includes(g.key) ? " soon" : ""}`}>
                <td className="vat-credit-on"><input type="checkbox" aria-label={`ПН №${g.nmr} від ${day(g.date)}`} checked={sel[g.key] != null} onChange={(e) => toggle(g, e.target.checked)} /></td>
                <td data-label="Постачальник">{shortName(g.supplierName) || g.supplier}<div className="muted small">{g.supplier}</div></td>
                <td data-label="ПН">№{g.nmr} від {day(g.date)}{g.rate !== 20 ? <div className="small">ставка {g.rate} %</div> : null}</td>
                <td data-label="Зареєстровано">{day(g.regDate)}</td>
                <td data-label="Доступно, без ПДВ">{uah2(g.available)}{g.used ? <div className="muted small">з {uah2(g.base)}, використано {uah2(g.used)}{g.allocated ? "*" : ""}</div> : null}</td>
                <td data-label="ПДВ">{uah2(g.availableVat)}</td>
                <td data-label="Включити до">{g.deadlineNote ? <span className="small">{g.deadlineNote}</span> : mm(g.deadline)}</td>
              </tr>))}</tbody>
          </table></div>
          {list.some((g) => g.allocated) && <p className="small muted">* У поданих Д1 використаний кредит указано сумою на постачальника за місяць, без номерів ПН; розподіляємо його на ПН від найдавніших.</p>}
        </details>
        </div>);
    })}</div>
  );
}

// Як вирішити, що включати: що обов'язково зараз, три сценарії з цифрами і принципи
function CreditAdvice({ advice, ctx, doc, update }) {
  const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(2).replace(".", ",")} %`);
  // Той самий набір (без огляду на порядок ключів і копійки округлення)
  const cur = doc.credit || {};
  const same = (a) => { const ka = Object.keys(a), kb = Object.keys(cur); return ka.length === kb.length && ka.every((k) => cur[k] != null && Math.abs(Number(cur[k]) - Number(a[k])) < 0.01); };
  const groups = Object.fromEntries(ctx.credit.map((g) => [g.key, g]));
  const names = (keys) => keys.map((k) => groups[k]).filter(Boolean).map((g) => `ПН №${g.nmr} від ${day(g.date)} (${shortName(g.supplierName) || g.supplier})`).join("; ");
  return (
    <div className="vat-advice">
      <p className="small"><b>Як вирішити, що включати.</b> Головне: невключений кредит згорає через 365 днів від дати ПН, а включений і невикористаний — переноситься на наступні місяці.
        Тому за замовчуванням безпечно включити все доступне, якщо немає причини відкласти (див. нижче).</p>
      <ol className="small">
        <li>{advice.mustNow.length ? <><b>Обов'язково цього місяця</b> — строк спливає: {names(advice.mustNow)}.</> : "Обов'язкових цього місяця немає — строк жодної ПН не спливає."}</li>
        {advice.soon.length > 0 && <li><b>Плануйте:</b> строк спливає протягом 3 місяців — {names(advice.soon)}.</li>}
        <li>Решту можна включити зараз або пізніше, до строку. Порівняння:</li>
      </ol>
      <div className="vat-scen">{advice.scenarios.map((x) => {
        const on = same(x.credit);
        return (
          <div key={x.key} className={`vat-scen-i${on ? " on" : ""}`}>
            <b>{x.title}</b>
            <span className="small">кредит {uah(x.creditVat)} · {x.payable ? <>до сплати <b>{uah(x.payable)}</b></> : <>до сплати 0</>}{x.carry ? ` · перенос ${uah(x.carry)}` : ""}</span>
            <span className="small muted">D {pct(advice.d)}{x.payable && x.dAfter != null ? ` → прогноз ${pct(x.dAfter)}` : " — без змін"}</span>
            {x.key === "min" && <span className="small muted">Решта кредиту чекатиме — не пропустіть строки.</span>}
            {x.key === "none" && x.payable > 0 && <span className="small muted">Це реальна сплата {uah(x.payable)}; D для ознак 2, 7, 9 має бути понад 5 %{x.dAfter != null && x.dAfter <= 0.05 ? " — цього не досягає" : ""}.</span>}
            <button type="button" className="btn btn-secondary btn-sm" disabled={on} onClick={() => update((d) => ({ ...d, credit: x.credit }))}>{on ? "Застосовано" : "Застосувати"}</button>
          </div>);
      })}</div>
      <details className="small"><summary>Чому так і коли відкладати</summary><ol>{advice.principles.map((t, i) => <li key={i}>{t}</li>)}</ol></details>
    </div>
  );
}

// --- Крок 3: результат ---
function StepResult({ ctx, res, go }) {
  const t = res.totals;
  const issues = [...res.unsupported.map((u) => ({ cls: "bad", text: `Не підтримується: ${u.title}` })), ...res.blocking.map((b) => ({ cls: "bad", text: b.title })),
    ...res.needs.map((n) => ({ cls: n.soft ? "info" : "warn", text: n.title }))];
  return (
    <section className="card">
      <h4>Ваш результат</h4>
      <p className="vat-big">{t.payable ? <>До сплати <b>{uah(t.payable)}</b> — подати до {day(ctx.deadline)}, сплатити до {day(ctx.payDeadline)}</> : <>Від'ємне значення <b>{uah(t.carry)}</b> переноситься на наступний місяць</>}</p>
      <ul className="small vat-explain">
        <li>ПДВ з продажів {uah(t.obligations)} — зареєстровані ПН{res.explain?.R90GB?.stoppedIncluded?.length ? ` і ${res.explain.R90GB.stoppedIncluded.length} зупинених, які ви включили` : ""}.</li>
        <li>Кредит {uah(t.credit)} = вибрані вхідні ПН {uah(res.rows.R101GB)}{res.rows.R111GB ? ` + імпорт ${uah(res.rows.R111GB)}` : ""}{res.rows.R140GB ? ` ${res.rows.R140GB < 0 ? "−" : "+"} коригування ${uah(Math.abs(res.rows.R140GB))}` : ""}{res.rows.R161GB ? ` + залишок з ${mm(ctx.prev?.period)} ${uah(res.rows.R161GB)}` : ""}.</li>
        {ctx.prev && <li>Залишок з попереднього місяця — з прийнятої декларації від {day(String(ctx.prev.dget).slice(0, 10))}.</li>}
      </ul>
      {issues.length ? (<><p><b>Що треба вирішити:</b></p><ul className="pn-issues-list">{issues.map((x, i) => <li key={i} className={x.cls}>{x.text}</li>)}</ul>
        <button type="button" className="linkish" onClick={() => go(2)}>← до операцій</button></>) : <p className="pn-ok">✓ Усе визначено — можна переглянути декларацію.</p>}
      <PayBlock ctx={ctx} res={res} />
    </section>
  );
}

// Сплата й показники — окремо від суми декларації: що задекларовано, скільки поповнити СЕА, реєстраційний ліміт, D.
// Сума декларації визначається операціями, а не бажаним навантаженням; доплачувати понад задеклароване не радимо
function PayBlock({ ctx, res }) {
  const p = ctx.pay || {}, due = res.totals.payable;
  const topUp = p.seaBalance == null ? null : Math.max(0, due - p.seaBalance);
  const pct = (v) => `${(v * 100).toFixed(2).replace(".", ",")} %`;
  const dAfter = p.d != null && p.t > 0 && due > 0 ? (p.s + due) / p.t : null;
  return (
    <section className="vat-pay" aria-labelledby="vat-pay-h">
      <h4 id="vat-pay-h">Сплата й показники</h4>
      <ol className="small">
        <li><b>Задекларовано до сплати:</b> {due ? <>{uah(due)} — сплатити до {day(ctx.payDeadline)}.</> : "нічого — від'ємне значення переноситься."}</li>
        {due > 0 && <li><b>Поповнити рахунок СЕА ПДВ:</b> {topUp == null ? "залишок на рахунку СЕА невідомий — оновіть розділ «СЕА»."
          : topUp > 0 ? <>щонайменше {uah(topUp)} (на рахунку {uah(p.seaBalance)} на {day(p.seaBalanceAt)}); ПДВ до бюджету перераховується з рахунку СЕА. Інші зобов'язання й списання, що ще не відбулись, можуть потребувати більше.</>
          : <>на рахунку вже {uah(p.seaBalance)} (на {day(p.seaBalanceAt)}) — вистачає, якщо інших списань не буде.</>}</li>}
        <li><b>Реєстраційний ліміт (ΣНакл):</b> {p.regLimit == null ? "невідомий — оновіть «СЕА»." : <>{uah(p.regLimit)} на {day(p.regLimitAt)} — це можливість реєструвати ПН, а не гроші на рахунку.</>}</li>
        <li><b>Показник D (СМКОР):</b> {p.d == null ? "невідомий — оновіть «СМКОР»." : <>зараз {pct(p.d)}{dAfter != null ? <>; після сплати цієї суми — прогноз {pct(dAfter)}, коли вона потрапить у розрахунок (ДПС враховує сплату із запізненням, з вікна випадають старі місяці)</> : ""}. Пороги для позитивної історії — у «Комплаєнсі».</>}</li>
      </ol>
      <p className="small muted">Сума декларації визначається операціями, а не бажаним навантаженням; рекомендації не обіцяють реєстрації ПН.</p>
    </section>
  );
}

// --- Крок 4: перегляд бланків (декларація, Д1, Д2) з того самого результату, що й XML ---
const A4 = 794;
function StepReview({ companyId, draftId, data, version, onReviewed }) {
  const { result: res, sha256, draft, needsOrg, context: ctx } = data;
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("decl");
  if (needsOrg) return <section className="card"><p className="notice">Немає жодної прийнятої декларації — реквізити платника для шапки взяти нізвідки. Оновіть «СМКОР».</p></section>;
  if (!res.ok) return <section className="card"><p className="notice">Декларацію ще не можна сформувати — поверніться до результату й вирішіть питання.</p></section>;
  const hasD1 = res.d1.t11.length + res.d1.t21.length + res.d1.t22.length > 0, hasD2 = Boolean(res.d2?.length);
  const cssHref = new URL(formCssUrl, location.href).href;
  const fillDate = new Date().toLocaleDateString("uk-UA");
  const html = tab === "d1" ? vatD1Html({ org: ctx.org, decl: res, fillDate, cssHref }) : tab === "d2" ? vatD2Html({ org: ctx.org, decl: res, fillDate, cssHref })
    : vatDeclarationHtml({ org: ctx.org, decl: res, regSum2001: draft.doc.regSum2001 ?? null, hasD1, hasD2, fillDate, cssHref });
  const rows = ROW_TITLES.filter(([, p]) => res.rows[`${p}GA`] || res.rows[`${p}GB`]);
  const base = `/api/companies/${companyId}/vat/drafts/${draftId}/xml`;
  const review = async () => {
    setErr("");
    try { await api(`/api/companies/${companyId}/vat/drafts/${draftId}/review`, { method: "POST", body: { sha256, version } }); onReviewed(); } catch (e) { setErr(e.message); }
  };
  const tabs = [["decl", "Декларація J0200126"], ...(hasD1 ? [["d1", "Додаток 1"]] : []), ...(hasD2 ? [["d2", "Додаток 2"]] : [])];
  return (
    <section className="card">
      <div className="tabs" role="tablist" aria-label="Документи комплекту">{tabs.map(([k, l]) => (
        <button type="button" role="tab" key={k} aria-selected={tab === k} className={tab === k ? "tab active" : "tab"} onClick={() => setTab(k)}>{l}</button>))}</div>
      <ScaledForm html={html} width={A4} title={`${tabs.find(([k]) => k === tab)[1]} — бланк затвердженої форми`} />
      <details className="small"><summary>Заповнені рядки декларації таблицею</summary>
        <div className="table-wrap"><table className="vat-rows">
          <thead><tr><th>Рядок</th><th>Зміст</th><th>Обсяг (А)</th><th>ПДВ (Б)</th></tr></thead>
          <tbody>{rows.map(([n, p, t]) => <tr key={p}><td>{n}</td><td>{t}</td><td>{res.rows[`${p}GA`] != null ? uah(res.rows[`${p}GA`]) : "—"}</td><td>{res.rows[`${p}GB`] != null ? uah(res.rows[`${p}GB`]) : "—"}</td></tr>)}</tbody>
        </table></div></details>
      <p className="row"><a className="btn btn-secondary btn-sm" href={`${base}?doc=decl`}>XML декларації</a>
        {hasD1 && <a className="btn btn-secondary btn-sm" href={`${base}?doc=d1`}>XML додатка 1</a>}
        {hasD2 && <a className="btn btn-secondary btn-sm" href={`${base}?doc=d2`}>XML додатка 2</a>}</p>
      {draft.reviewed?.sha256 === sha256 ? <p className="pn-ok">✓ Переглянуто {day(draft.reviewed.at)}. Будь-яка зміна скине підтвердження.</p>
        : <button type="button" className="btn" onClick={review}>Я переглянув(-ла) декларацію{hasD1 || hasD2 ? " і додатки" : ""}</button>}
      {err && <p className="error small">{err}</p>}
    </section>
  );
}
